import addToMailchimp from "gatsby-plugin-mailchimp";
import React, { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@global";
import { LAPTOP_BREAKPOINT, TABLET_BREAKPOINT } from "@util/constants";
import { removeTags } from "@util/helper";
import { Container, P } from "@util/standard";

interface Props {
  stack?: boolean;
  isDark?: boolean;
}

interface State {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const StyledInput = styled.input<{ isDark?: boolean }>`
  background: transparent;
  border: none;
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: ${({ isDark }) =>
    isDark ? "var(--theme-primary-color)" : "white"};
  height: 30px;
  margin-right: 10px;
  color: ${({ isDark }) => (isDark ? "var(--theme-primary-color)" : "white")};
  &::placeholder {
    font-size: 14px;
    font-weight: "normal";
    color: ${({ isDark }) => (isDark ? "var(--theme-primary-color)" : "white")};
  }
  &:focus {
    outline: none;
    border-bottom-color: ${({ isDark }) =>
      isDark ? "white" : "var(--theme-primary-color)"};
  }
`;

const HalfStyledInput = styled(StyledInput)`
  width: 49%;
`;

const StyledForm = styled.form<{ stack?: boolean }>`
  margin: 0 0 0 70px;
  width: 60%;
  display: flex;
  justify-content: space-between;
  ${({ stack }) =>
    stack &&
    `
    margin: 0;
    flex-flow: column nowrap;
    width: 100%;
  `}
  @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px) {
    margin: 0 0 0 50px;
    ${({ stack }) =>
      stack &&
      `
    margin: 0;
  `}
  }
  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    margin: 0;
    width: 100%;
    flex-flow: column nowrap;
    input[name="firstName"],
    input[name="lastName"] {
      width: 50%;
    }
  }
`;

const ButtonContainer = styled(Container)<{ stack?: boolean }>`
  ${({ stack }) =>
    stack
      ? `
    margin: 45px 0 0;
  `
      : `
    margin: 0 0 0 70px;
  `}
  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    justify-content: flex-start;
    width: 100%;
    margin: 30px 0 0;
  }
`;

const NewsletterForm = ({ stack, isDark }: Props) => {
  const initialState: State = {
    firstName: "",
    lastName: "",
    email: "",
  };

  const [state, setState] = useState<State>(initialState);
  const [result, setResult] = useState();

  const handleInputChange = (event: any) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    setState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    addToMailchimp(state.email, {
      FNAME: state.firstName,
      LNAME: state.lastName,
    }) // listFields are optional if you are only capturing the email address.
      .then(data => {
        // I recommend setting data to React state
        // but you can do whatever you want (including ignoring this `then()` altogether)

        const strippedData = removeTags(data.msg);
        setResult(data);
        if (data.result == "error") {
          if (
            strippedData &&
            strippedData.indexOf("is already subscribed") > -1
          ) {
            toast.error(
              `${state.email} is already subscribed to our newsletter.`
            );
          } else {
            toast.error(strippedData);
          }
        }
      })
      .catch(e => {
        // unnecessary because Mailchimp only ever
        // returns a 200 status code
        // see below for how to handle errors
        setResult({
          result: "error",
          msg: "An unexpected error occured, please try again later or disable your ad blocker",
        });
        toast.error(
          "An unexpected error occured, please try again later or disable your ad blocker"
        );
      });
  };

  return (
    <>
      {result && result.result == "success" ? (
        <Container>
          <P color="white">{result.msg}</P>
        </Container>
      ) : (
        <StyledForm stack={stack} onSubmit={handleSubmit}>
          <Container
            flexDirection="column"
            width="100%"
            justifyContent="center"
          >
            <Container margin={stack ? "0 0 10px" : "0"}>
              <HalfStyledInput
                placeholder="First Name"
                name="firstName"
                value={state.firstName}
                onChange={handleInputChange}
                isDark={isDark}
              />
              <HalfStyledInput
                placeholder="Last Name"
                name="lastName"
                value={state.lastName}
                onChange={handleInputChange}
                isDark={isDark}
              />
            </Container>
            <Container flexDirection="column" margin="20px 0">
              <StyledInput
                placeholder="Email Address"
                name="email"
                value={state.email}
                onChange={handleInputChange}
                isDark={isDark}
              />
            </Container>
          </Container>

          <ButtonContainer alignItems="center" stack={stack}>
            <Button text="Sign up" theme="white-outline" />
          </ButtonContainer>
        </StyledForm>
      )}
    </>
  );
};

export default NewsletterForm;
